import type { StampInSheetData, UserSheetHistoryData } from '@/schemas/v2/sheet'

/** 行データの作成に使うスタンプの項目（ホームの mappedSheets のスタンプもそのまま渡せる） */
type StampForStatus = Pick<StampInSheetData, 'id' | 'name' | 'point' | 'spots'>

/** スポットごとの獲得状況の 1 行（#3582） */
export type SpotPointStatusItem = {
  /** 行の key（`spot-{spotId}`。スポットが紐づかないスタンプは `stamp-{stampId}`） */
  key: string
  /** 左に出すスポット名（スポットが紐づかないスタンプはスタンプ名） */
  label: string
  /** このスポットで獲得したポイントの合計（未獲得は 0）。使ったポイントは差し引かない */
  earnedPoint: number
  /** このスポットで獲得できるポイント（まとめたスタンプの設定値の合計） */
  availablePoint: number
  /** タップ時の移動先スポット。null ならタップ不可 */
  spotId: number | null
}

/**
 * ポイントラリーのシートについて、スポットごとの獲得状況を作る（#3582）。
 *
 * - スタンプは先頭のスポット（spots[0]・ID が最も小さい公開スポット）の行にまとめる。2 つ目以降のスポットには数えない
 * - 獲得状況に関わらずすべて返す。並びはシートのスタンプ順で、スポットが最初に出てきた位置
 * - 獲得したポイントは history の実績値を合計する。point が null（旧データ・0pt の記録）のときはスタンプの設定値で補う
 * - stamps に無いスタンプの履歴は数えない。シート一覧 API はすべてのスポットが非公開のスタンプを stamps から除くため、その獲得分はどの行にも出ない
 *
 * @param canNavigate false のときは spotId を null にしてスポット名をタップ不可にする（canNavigateToSpot・#2972）
 */
export function buildSpotPointStatusItems(
  stamps: readonly StampForStatus[],
  history: readonly UserSheetHistoryData[],
  canNavigate: boolean,
): SpotPointStatusItem[] {
  const masterPoint = new Map(stamps.map((s) => [s.id, s.point]))
  const earnedByStamp = new Map<number, number>()
  for (const h of history) {
    const point = h.point ?? masterPoint.get(h.stampId) ?? 0
    earnedByStamp.set(h.stampId, (earnedByStamp.get(h.stampId) ?? 0) + point)
  }

  // Map は挿入順を保つので、シートのスタンプ順でスポットが最初に出てきた位置に並ぶ
  const items = new Map<string, SpotPointStatusItem>()
  for (const stamp of stamps) {
    const spot = stamp.spots[0]
    const key = spot ? `spot-${spot.id}` : `stamp-${stamp.id}`
    const earned = earnedByStamp.get(stamp.id) ?? 0
    const current = items.get(key)
    if (current) {
      current.earnedPoint += earned
      current.availablePoint += stamp.point
      continue
    }
    items.set(key, {
      key,
      label: spot?.name ?? stamp.name,
      earnedPoint: earned,
      availablePoint: stamp.point,
      spotId: canNavigate && spot ? spot.id : null,
    })
  }
  return [...items.values()]
}
